import type { GridItemChangeEvent } from "@tql/ui-components-react/kendo/grid";

import type { RoutingGuideEnumsType } from "~/shared/schemas/routing-guide-schema.ts";

import { getLabelsForSelector } from "./get-labels-for-selector.ts";
import { getOperators } from "./get-operators.ts";
import type { RowUpdate } from "./types.ts";

type SetRows<T> = React.Dispatch<React.SetStateAction<T[]>>;

export const applyRowChange = <T extends { id: number }>(
	event: GridItemChangeEvent | RowUpdate<T>,
	setRows: SetRows<T>,
	enums?: RoutingGuideEnumsType,
): void => {
	const { field, dataItem, value } = event as RowUpdate<T>;
	if (!field) return;
	setRows((prev) =>
		prev.map((item) =>
			item.id === dataItem.id ? updateRowItem(item, field, value, enums) : item,
		),
	);
};

export const removeRow = <T extends { id: number }>(
	id: number,
	setRows: SetRows<T>,
): void => {
	setRows((prev) =>
		prev.length > 1 ? prev.filter((item) => item.id !== id) : prev,
	);
};

export const updateRowItem = <T extends { id: number }>(
	item: T,
	field: keyof T,
	value: unknown,
	enums?: RoutingGuideEnumsType,
): T => {
	// Handle Category (Selector) Changes
	// If the user changes the "Origin", "Destination", etc., we need to reset all input fields
	if ((field as string) === "fieldSelector") {
		const nextSelector = value as string;
		const labels = getLabelsForSelector(nextSelector, enums);
		const operators = getOperators(nextSelector, enums);
		const autoOperator =
			operators.length === 1 ? (operators[0]?.name ?? "") : "";

		// Clear out all potentially existing fields (field1-field6) to ensure a clean state
		const cleanItem = { ...item } as Record<string, unknown>;
		for (const num of [1, 2, 3, 4, 5, 6]) {
			delete cleanItem[`field${num}`];
		}

		// Map the new labels to our field structure
		// Both Rule and CarrierRule store fieldN as { label, value } objects,
		// so always reset to the nested object shape (existing rules loaded
		// from the API may have field1 = null, which would otherwise cause
		// us to fall back to plain strings).
		const fieldResets = Object.fromEntries(
			labels.map((label, i) => {
				const fieldName = `field${i + 1}`;
				return [fieldName, { label, value: "" }];
			}),
		);

		return {
			...cleanItem,
			fieldSelector: nextSelector,
			operator: autoOperator,
			...fieldResets,
		} as unknown as T;
	}

	// Handle Field Cell Updates
	const fieldStr = field as string;
	if (/^field\d+$/.test(fieldStr)) {
		const currentFieldValue = item[field];
		const existingLabel =
			currentFieldValue &&
			typeof currentFieldValue === "object" &&
			"label" in currentFieldValue
				? (currentFieldValue as { label: string }).label
				: undefined;

		let label = existingLabel;
		if (label === undefined) {
			const fieldIndex = Number.parseInt(fieldStr.replace("field", ""), 10) - 1;
			const selectorName =
				((item as Record<string, unknown>)["fieldSelector"] as string) ?? "";
			const labels = getLabelsForSelector(selectorName, enums);
			label = labels[fieldIndex] ?? "";
		}

		return {
			...item,
			[field]: { label, value: value as string },
		};
	}

	// Handle Standard Primitive Updates
	// Used for simple fields like "condition", "operator"
	return { ...item, [field]: value };
};
